import PropTypes from "prop-types";
import { PureComponent } from "react";

import { Helmet } from "@unhead/react/helmet";

import Base from "flavours/glitch/components/modal_root";
import { AltTextModal } from "flavours/glitch/features/alt_text_modal";
import {
  MuteModal,
  BlockModal,
  DomainBlockModal,
  ReportModal,
  ReportCollectionModal,
  SettingsModal,
  EmbedModal,
  ListAdder,
  CompareHistoryModal,
  FilterModal,
  InteractionModal,
  SubscribedLanguagesModal,
  ClosedRegistrationsModal,
  IgnoreNotificationsModal,
  AnnualReportModal,
} from "flavours/glitch/features/ui/util/async-components";

import { ActionsModal } from "./actions_modal";
import AudioModal from "./audio_modal";
import { BoostModal } from "./boost_modal";
import Bundle from "./bundle";
import {
  ConfirmationModal,
  ConfirmDeleteStatusModal,
  ConfirmDeleteListModal,
  ConfirmDeleteCollectionModal,
  ConfirmReplyModal,
  ConfirmEditStatusModal,
  ConfirmUnblockModal,
  ConfirmUnfollowModal,
  ConfirmWithdrawRequestModal,
  ConfirmClearNotificationsModal,
  ConfirmLogOutModal,
  ConfirmFollowToListModal,
  ConfirmMissingAltTextModal,
  ConfirmRevokeQuoteModal,
  QuietPostQuoteInfoModal,
} from "./confirmation_modals";
import DeprecatedSettingsModal from "./deprecated_settings_modal";
import DoodleModal from "./doodle_modal";
import { FavouriteModal } from "./favourite_modal";
import { ImageModal } from "./image_modal";
import { MediaModal } from "./media_modal";
import { ModalPlaceholder } from "./modal_placeholder";
import VideoModal from "./video_modal";
import { VisibilityModal } from "./visibility_modal";
import { PrivateQuoteNotify } from "./confirmation_modals/private_quote_notify";

export const MODAL_COMPONENTS = {
  MEDIA: () => Promise.resolve({ default: MediaModal }),
  VIDEO: () => Promise.resolve({ default: VideoModal }),
  AUDIO: () => Promise.resolve({ default: AudioModal }),
  IMAGE: () => Promise.resolve({ default: ImageModal }),
  BOOST: () => Promise.resolve({ default: BoostModal }),
  FAVOURITE: () => Promise.resolve({ default: FavouriteModal }),
  DOODLE: () => Promise.resolve({ default: DoodleModal }),
  CONFIRM: () => Promise.resolve({ default: ConfirmationModal }),
  CONFIRM_DELETE_STATUS: () => Promise.resolve({ default: ConfirmDeleteStatusModal }),
  CONFIRM_DELETE_LIST: () => Promise.resolve({ default: ConfirmDeleteListModal }),
  CONFIRM_DELETE_COLLECTION: () => Promise.resolve({ default: ConfirmDeleteCollectionModal }),
  CONFIRM_REPLY: () => Promise.resolve({ default: ConfirmReplyModal }),
  CONFIRM_EDIT_STATUS: () => Promise.resolve({ default: ConfirmEditStatusModal }),
  CONFIRM_UNBLOCK: () => Promise.resolve({ default: ConfirmUnblockModal }),
  CONFIRM_UNFOLLOW: () => Promise.resolve({ default: ConfirmUnfollowModal }),
  CONFIRM_WITHDRAW_REQUEST: () => Promise.resolve({ default: ConfirmWithdrawRequestModal }),
  CONFIRM_CLEAR_NOTIFICATIONS: () => Promise.resolve({ default: ConfirmClearNotificationsModal }),
  CONFIRM_LOG_OUT: () => Promise.resolve({ default: ConfirmLogOutModal }),
  CONFIRM_FOLLOW_TO_LIST: () => Promise.resolve({ default: ConfirmFollowToListModal }),
  CONFIRM_MISSING_ALT_TEXT: () => Promise.resolve({ default: ConfirmMissingAltTextModal }),
  CONFIRM_PRIVATE_QUOTE_NOTIFY: () => Promise.resolve({ default: PrivateQuoteNotify }),
  CONFIRM_REVOKE_QUOTE: () => Promise.resolve({ default: ConfirmRevokeQuoteModal }),
  CONFIRM_QUIET_QUOTE: () => Promise.resolve({ default: QuietPostQuoteInfoModal }),
  MUTE: MuteModal,
  BLOCK: BlockModal,
  DOMAIN_BLOCK: DomainBlockModal,
  REPORT: ReportModal,
  REPORT_COLLECTION: ReportCollectionModal,
  COLLECTION_ADDER: () =>
    import("@/flavours/glitch/features/collection_adder").then((module) => ({
      default: module.CollectionAdder,
    })),
  SHARE_COLLECTION: () =>
    import("@/flavours/glitch/features/collections/components/share_modal").then((module) => ({
      default: module.CollectionShareModal,
    })),
  REVOKE_COLLECTION_INCLUSION: () =>
    import("@/flavours/glitch/features/collections/detail/revoke_collection_inclusion_modal").then(
      (module) => ({ default: module.RevokeCollectionInclusionModal }),
    ),
  SETTINGS: SettingsModal,
  DEPRECATED_SETTINGS: () => Promise.resolve({ default: DeprecatedSettingsModal }),
  ACTIONS: () => Promise.resolve({ default: ActionsModal }),
  EMBED: EmbedModal,
  FOCAL_POINT: () => Promise.resolve({ default: AltTextModal }),
  LIST_ADDER: ListAdder,
  COMPARE_HISTORY: CompareHistoryModal,
  FILTER: FilterModal,
  SUBSCRIBED_LANGUAGES: SubscribedLanguagesModal,
  INTERACTION: InteractionModal,
  CLOSED_REGISTRATIONS: ClosedRegistrationsModal,
  IGNORE_NOTIFICATIONS: IgnoreNotificationsModal,
  ANNUAL_REPORT: AnnualReportModal,
  WELCOME_DIGEST: () =>
    import("@/flavours/glitch/features/ui/components/welcome_digest_modal").then((module) => ({
      default: module.WelcomeDigestModal,
    })),
  COMPOSE_PRIVACY: () => Promise.resolve({ default: VisibilityModal }),
  ACCOUNT_NOTE: () =>
    import("@/flavours/glitch/features/account_timeline/modals/note_modal").then((module) => ({
      default: module.AccountNoteModal,
    })),
  ACCOUNT_FIELD_OVERFLOW: () =>
    import("@/flavours/glitch/features/account_timeline/modals/field_modal").then((module) => ({
      default: module.AccountFieldModal,
    })),
  ACCOUNT_JOIN_DATE: () =>
    import("@/flavours/glitch/features/account_timeline/modals/join_modal").then((module) => ({
      default: module.AccountJoinModal,
    })),
  ACCOUNT_EDIT_NAME: accountEditModal("NameModal"),
  ACCOUNT_EDIT_BIO: accountEditModal("BioModal"),
  ACCOUNT_EDIT_PROFILE_DISPLAY: accountEditModal("ProfileDisplayModal"),
  ACCOUNT_EDIT_VERIFY_LINKS: accountEditModal("VerifiedModal"),
  ACCOUNT_EDIT_FIELD_EDIT: accountEditModal("EditFieldModal"),
  ACCOUNT_EDIT_FIELD_DELETE: accountEditModal("DeleteFieldModal"),
  ACCOUNT_EDIT_FIELDS_REORDER: accountEditModal("ReorderFieldsModal"),
  ACCOUNT_EDIT_IMAGE_ALT: accountEditModal("ImageAltModal"),
  ACCOUNT_EDIT_IMAGE_DELETE: accountEditModal("ImageDeleteModal"),
  ACCOUNT_EDIT_IMAGE_UPLOAD: accountEditModal("ImageUploadModal"),
  ACCOUNT_HIDE_FEATURED_TAB: () =>
    import("@/flavours/glitch/features/ui/components/confirmation_modals/hide_featured_tab").then(
      (module) => ({ default: module.ConfirmHideFeaturedTabModal }),
    ),
};

/** @arg {keyof import('@/flavours/glitch/features/account_edit/modals')} type */
function accountEditModal(type) {
  return () =>
    import("@/flavours/glitch/features/account_edit/modals").then((module) => ({
      default: module[type],
    }));
}

export default class ModalRoot extends PureComponent {
  static propTypes = {
    type: PropTypes.string,
    props: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    ignoreFocus: PropTypes.bool,
  };

  state = {
    backgroundColor: null,
  };

  setBackgroundColor = (color) => {
    this.setState({ backgroundColor: color });
  };

  renderLoading = () => {
    const { onClose } = this.props;

    return <ModalPlaceholder loading onClose={onClose} />;
  };

  renderError = (props) => {
    const { onClose } = this.props;

    return <ModalPlaceholder {...props} onClose={onClose} />;
  };

  handleClose = (ignoreFocus = false) => {
    const { onClose } = this.props;
    const message = this._modal?.getCloseConfirmationMessage?.();
    onClose(message, ignoreFocus);
  };

  setModalRef = (c) => {
    this._modal = c;
  };

  // prevent closing of modal when clicking the overlay
  noop = () => {};

  render() {
    const { type, props, ignoreFocus } = this.props;
    const { backgroundColor } = this.state;
    const visible = !!type;

    return (
      <Base
        backgroundColor={backgroundColor}
        onClose={props && props.noClose ? this.noop : this.handleClose}
        noEsc={props ? props.noEsc : false}
        ignoreFocus={ignoreFocus}
      >
        {visible && (
          <>
            <Bundle
              key={type}
              fetchComponent={MODAL_COMPONENTS[type]}
              loading={this.renderLoading}
              error={this.renderError}
              renderDelay={200}
            >
              {(SpecificComponent) => {
                return (
                  <SpecificComponent
                    {...props}
                    onChangeBackgroundColor={this.setBackgroundColor}
                    onClose={this.handleClose}
                    ref={this.setModalRef}
                  />
                );
              }}
            </Bundle>

            <Helmet>
              <meta name="robots" content="noindex" />
            </Helmet>
          </>
        )}
      </Base>
    );
  }
}
